import { MessagingSettingsPage } from '@/pages/messaging-settings/MessagingSettingsPage';
import { MessagesPage } from '@/pages/messages/MessagesPage';
import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { permissionKeys } from '@/utility';
import { lazyWithPreload } from '@gokrew/ui/utility/lazyWithPreload';
import { OnboardingRoute } from '@gokrew/models/onboarding';
import { OnboardingFeatureKey } from '@gokrew/models/company';
import type { PermissionScope } from '@/types/permissions';

// ─── Priority: bundled in main chunk ─────────────────────────────────────────
import { NotesIndexRedirect } from '@/pages/notes/NotesIndexRedirect';
import { LoginV2Page } from '@/pages/auth-v2/pages/LoginPage';
import { ResetPasswordV2Page } from '@/pages/auth-v2/pages/ResetPasswordPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ModuleGuard } from '@/components/ModuleGuard';
import { PerformanceGuard } from '@/components/PerformanceGuard';
import { TrainingGuard } from '@/components/TrainingGuard';
import { PayrollExportGuard } from '@/components/PayrollExportGuard';
import { BillingGuard } from '@/components/billing/BillingGuard';
import { RedirectToDefaultRoute } from '@/components/RedirectToDefaultRoute';

// ─── Lazy: code-split into separate chunks ────────────────────────────────────
const ForgotPasswordV2Page = lazy(() =>
  import('@/pages/auth-v2/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordV2Page,
  }))
);
const SignupV2Page = lazy(() =>
  import('@/pages/auth-v2').then((m) => ({ default: m.SignupV2Page }))
);
const VerifyPage = lazy(() =>
  import('@/pages/auth-v2/VerifyPage').then((m) => ({ default: m.VerifyPage }))
);
const SelectWorkspacePage = lazy(() =>
  import('@/pages/auth-v2/pages/SelectWorkspacePage').then((m) => ({
    default: m.SelectWorkspacePage,
  }))
);
const CompanyPage = lazyWithPreload(
  () => import('@/pages/auth/onboarding/CompanyPage'),
  'CompanyPage'
);
const LocationSetupPage = lazyWithPreload(
  () => import('@/pages/auth/onboarding/LocationSetupPage')
);
const FeaturesPage = lazyWithPreload(
  () => import('@/pages/auth/onboarding/FeaturesPage'),
  'FeaturesPage'
);
const LogoSetupPage = lazyWithPreload(
  () => import('@/pages/auth/onboarding/LogoSetup'),
  'LogoSetup'
);
const OnboardingLayout = lazyWithPreload(
  () => import('@/pages/auth/onboarding/OnboardingLayout'),
  'OnboardingLayout'
);
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ReleaseNotesPage = lazyWithPreload(
  () => import('@/pages/release-notes/ReleaseNotesPage')
);

// ─── lazyWithPreload: lazy + preload in one declaration ──────────────────────

// Employees
const EmployeesPage = lazyWithPreload(
  () => import('@/pages/employees'),
  'EmployeesPage'
);
const CompanyDocumentRequestsPage = lazyWithPreload(
  () => import('@/pages/employees/document-requests'),
  'CompanyDocumentRequestsPage'
);
const EmployeeDetailsPage = lazyWithPreload(
  () => import('@/pages/employees/EmployeeDetailsPage')
);
const EmployeeInformation = lazyWithPreload(
  () => import('@/pages/employees/information/EmployeeInformation'),
  'EmployeeInformation'
);
const EmployeeEducation = lazyWithPreload(
  () => import('@/pages/employees/education/EmployeeEducation'),
  'EmployeeEducation'
);
const EmployeeDocuments = lazyWithPreload(
  () => import('@/pages/employees/documents/EmployeeDocuments'),
  'EmployeeDocuments'
);
const EmployeeLeaveHistory = lazyWithPreload(
  () => import('@/pages/employees/leave-history/EmployeeLeaveHistory'),
  'EmployeeLeaveHistory'
);
const EmployeeFamily = lazyWithPreload(
  () => import('@/pages/employees/family/EmployeeFamily'),
  'EmployeeFamily'
);
const EmployeeNotes = lazyWithPreload(
  () => import('@/pages/employees/notes/EmployeeNotes'),
  'EmployeeNotes'
);
const EmployeeTrainings = lazyWithPreload(
  () => import('@/pages/employees/trainings/EmployeeTrainings')
);
const EmployeePerformance = lazyWithPreload(
  () => import('@/pages/employees/performance/EmployeePerformance')
);
const EmployeeAssets = lazyWithPreload(
  () => import('@/pages/employees/assets/EmployeeAssets'),
  'EmployeeAssets'
);
const EmployeeSalary = lazyWithPreload(
  () => import('@/pages/employees/salary/employeeSalary'),
  'EmployeeSalary'
);
const EmployeeBankDetails = lazyWithPreload(
  () => import('@/pages/employees/banking/EmployeeBankDetails'),
  'EmployeeBankDetails'
);
const EmployeeCompensationPage = lazyWithPreload(
  () => import('@/pages/employees/compensation/EmployeeCompensationPage')
);
const EmployeeOnboarding = lazyWithPreload(
  () => import('@/pages/employees/onboarding-offboarding/EmployeeOnboarding'),
  'EmployeeOnboarding'
);
const EmployeeOffboarding = lazyWithPreload(
  () => import('@/pages/employees/onboarding-offboarding/EmployeeOffboarding'),
  'EmployeeOffboarding'
);

const EmployeeTasks = lazyWithPreload(
  () => import('@/pages/employees/tasks/EmployeeTasks'),
  'EmployeeTasks'
);
const EmployeeActivityLogs = lazyWithPreload(
  () => import('@/pages/employees/activity/EmployeeActivityLogs'),
  'EmployeeActivityLogs'
);
const DocumentTypeConfig = lazyWithPreload(
  () => import('@/pages/employees/documents/document-types/DocumentTypeConfig')
);
const EmployeeImportJobsPage = lazyWithPreload(
  () => import('@/pages/employees/employees-bulk-import/EmployeeImportJobsPage')
);
const EmployeeImportDetailsPage = lazyWithPreload(
  () =>
    import('@/pages/employees/employees-bulk-import/EmployeeImportDetailsPage')
);
const RecruitingPage = lazyWithPreload(
  () => import('@/pages/recruiting/RecruitingPage')
);
const CandidateProfilePage = lazyWithPreload(
  () => import('@/pages/recruiting/CandidateProfilePage/CandidateProfilePage')
);

// Organization
const DesignationPage = lazyWithPreload(
  () => import('@/pages/designation'),
  'DesignationPage'
);
const DepartmentPage = lazyWithPreload(
  () => import('@/pages/department'),
  'DepartmentPage'
);
const DepartmentDetailsPage = lazyWithPreload(
  () => import('@/pages/department'),
  'DepartmentDetailsPage'
);
const ReportingLinesPage = lazyWithPreload(
  () => import('@/pages/reporting-lines'),
  'ReportingLinesPage'
);
const ReportingExceptionsPage = lazyWithPreload(
  () => import('@/pages/reporting-exceptions'),
  'ReportingExceptionsPage'
);
const EmployeeCustomFieldsPage = lazyWithPreload(
  () => import('@/pages/employee-custom-fields'),
  'EmployeeCustomFieldsPage'
);
const OrganizationPage = lazyWithPreload(
  () => import('@/pages/organization'),
  'OrganizationPage'
);
const OrganizationModulesPage = lazyWithPreload(
  () => import('@/pages/organization/modules/ModulesPage'),
  'ModulesPage'
);
const BillingPage = lazyWithPreload(
  () => import('@/pages/billing/BillingPage'),
  'BillingPage'
);
const RemindersPage = lazyWithPreload(
  () => import('@/pages/reminders'),
  'RemindersPage'
);
// E-signatures (docs/product/e-signatures-documenso.md)
const EsignaturesPage = lazyWithPreload(
  () => import('@/pages/esignatures/EsignaturesPage'),
  'EsignaturesPage'
);
const EsignatureTemplatesTab = lazyWithPreload(
  () => import('@/pages/esignatures/templates/TemplatesTab'),
  'TemplatesTab'
);
const EsignatureRequestsTab = lazyWithPreload(
  () => import('@/pages/esignatures/requests/RequestsTab'),
  'RequestsTab'
);
const EsignatureNewRequestPage = lazyWithPreload(
  () => import('@/pages/esignatures/requests/NewRequestPage'),
  'NewRequestPage'
);
const EsignatureRequestDetailPage = lazyWithPreload(
  () => import('@/pages/esignatures/requests/RequestDetailPage'),
  'RequestDetailPage'
);
const EsignatureBroadcastsTab = lazyWithPreload(
  () => import('@/pages/esignatures/broadcasts/BroadcastsTab'),
  'BroadcastsTab'
);
const EsignatureBroadcastDetailPage = lazyWithPreload(
  () => import('@/pages/esignatures/broadcasts/BroadcastDetailPage'),
  'BroadcastDetailPage'
);
const EsignatureTemplateEditorPage = lazyWithPreload(
  () => import('@/pages/esignatures/templates/TemplateEditorPage'),
  'TemplateEditorPage'
);
const CompanyAdminPage = lazyWithPreload(
  () => import('@/pages/company-admin'),
  'CompanyAdminPage'
);
const RolesPage = lazyWithPreload(
  () => import('@/pages/roles/RolesPage'),
  'RolesPage'
);
const CompanyAdminDetailPage = lazyWithPreload(
  () => import('@/pages/company-admin/CompanyAdminDetailPage'),
  'CompanyAdminDetailPage'
);
const CompanyAdminProfileTab = lazyWithPreload(
  () => import('@/pages/company-admin/ProfileDetailsTab'),
  'ProfileDetailsTab'
);
const CompanyAdminOnboardingTasksTab = lazyWithPreload(
  () => import('@/pages/company-admin/AssignedTasksTab'),
  'OnboardingTasksTab'
);
const CompanyAdminOffboardingTasksTab = lazyWithPreload(
  () => import('@/pages/company-admin/AssignedTasksTab'),
  'OffboardingTasksTab'
);
const CompanyAdminSingleTasksTab = lazyWithPreload(
  () => import('@/pages/company-admin/AssignedTasksTab'),
  'SingleTasksTab'
);
const CompanyAdminManagedEmployeesTab = lazyWithPreload(
  () => import('@/pages/company-admin/ManagedEmployeesTab'),
  'ManagedEmployeesTab'
);
const CompanyAdminActivityTabRoute = lazyWithPreload(
  () => import('@/pages/company-admin/AdminActivityTab'),
  'AdminActivityTab'
);
const EditRolePage = lazyWithPreload(
  () => import('@/pages/roles/EditRolePage'),
  'EditRolePage'
);
const CompanyInfoPage = lazyWithPreload(
  () => import('@/pages/company-info'),
  'CompanyInfoPage'
);
const CompanyInfoGeneralTab = lazyWithPreload(
  () => import('@/pages/company-info/CompanyInfoGeneralTab'),
  'CompanyInfoGeneralTab'
);
const CompanyActivityLogsPage = lazyWithPreload(
  () => import('@/pages/company-activity/CompanyActivityLogsPage'),
  'CompanyActivityLogsPage'
);
const BenefitPlansPage = lazyWithPreload(
  () => import('@/pages/benefit-plans/BenefitPlansPage'),
  'BenefitPlansPage'
);
const VendorsPage = lazyWithPreload(
  () => import('@/pages/vendors/VendorsPage'),
  'VendorsPage'
);

// Employee access
const EmployeeSettingsTabs = lazyWithPreload(
  () => import('@/pages/employee-access/EmployeeSettingsTabs'),
  'EmployeeSettingsTabs'
);
const EmployeeAccessPage = lazyWithPreload(
  () => import('@/pages/employee-access/EmployeeAccessPage'),
  'EmployeeAccessPage'
);
const ApproveRequestsPage = lazyWithPreload(
  () => import('@/pages/employee-access/ApproveRequestsPage'),
  'ApproveRequestsPage'
);

// Branches
const BranchPage = lazyWithPreload(
  () => import('@/pages/branches/BranchPage'),
  'BranchPage'
);
const BranchDetailPage = lazyWithPreload(
  () => import('@/pages/branches/BranchDetailPage'),
  'BranchDetailPage'
);

// Integrations
const IntegrationsPage = lazyWithPreload(
  () => import('@/pages/integrations/IntegrationsPage'),
  'IntegrationsPage'
);
const McpIntegrationPage = lazyWithPreload(
  () => import('@/pages/integrations/McpIntegrationPage/McpIntegrationPage'),
  'McpIntegrationPage'
);

// Assets
const BrandPage = lazyWithPreload(
  () => import('@/pages/asset-brands/BrandsPage'),
  'BrandPage'
);
const AssetTypesPage = lazyWithPreload(
  () => import('@/pages/asset-types/AssetTypesPage'),
  'AssetTypesPage'
);
const TypeSettingsPage = lazyWithPreload(
  () => import('@/pages/asset-types/config/TypeSettingsPage'),
  'TypeSettingsPage'
);
const AssetsPage = lazyWithPreload(
  () => import('@/pages/company-asset/AssetsPage'),
  'AssetsPage'
);
const AssetDetailsPage = lazyWithPreload(
  () => import('@/pages/company-asset/AssetDetailsPage'),
  'AssetDetailsPage'
);
const AssetForm = lazyWithPreload(
  () => import('@/pages/company-asset/AssetForm'),
  'AssetForm'
);
const AssetEditPage = lazyWithPreload(
  () => import('@/pages/company-asset/AssetEditPage'),
  'AssetEditPage'
);

// Leave management
const ApprovalRoutingPage = lazyWithPreload(
  () => import('@/pages/leave-management/approvals/ApprovalRoutingPage'),
  'ApprovalRoutingPage'
);
const LeaveManagementPage = lazyWithPreload(
  () => import('@/pages/leave-management'),
  'LeaveManagementPage'
);
const OverviewPage = lazyWithPreload(
  () => import('@/pages/leave-management/overview'),
  'OverviewPage'
);
const PoliciesPage = lazyWithPreload(
  () => import('@/pages/leave-management/policies/PoliciesPage'),
  'PoliciesPage'
);
const PolicyFormPage = lazyWithPreload(
  () => import('@/pages/leave-management/policies/policy-form'),
  'PolicyFormPage'
);
const LeaveWalletsPage = lazyWithPreload(
  () =>
    import('@/pages/leave-management/overview/LeaveWallets/LeaveWalletsPage'),
  'LeaveWalletsPage'
);
const GrantLeaveHistoryPage = lazyWithPreload(
  () =>
    import('@/pages/leave-management/policies/grantLeaveHistory/GrantLeaveHistoryPage'),
  'GrantLeaveHistoryPage'
);
const HolidaysPage = lazyWithPreload(
  () => import('@/pages/leave-management/holidays'),
  'HolidaysPage'
);
const LeaveCalendarPage = lazyWithPreload(
  () => import('@/pages/leave-management/calendar/LeaveCalendarPage'),
  'LeaveCalendarPage'
);

// Files
const FilesPage = lazyWithPreload(() => import('@/pages/files/FilesPage'));
const FolderDetails = lazyWithPreload(
  () => import('@/pages/files/summary/FolderDetails')
);
const FilesBin = lazyWithPreload(() => import('@/pages/files/bin/FilesBin'));

// Notes
const NotesPage = lazyWithPreload(() => import('@/pages/notes/NotesPage'));
const AdminNotesPage = lazyWithPreload(
  () => import('@/pages/notes/admin/AdminNotesPage')
);
const EmployeeNotesPage = lazyWithPreload(
  () => import('@/pages/notes/employee/EmployeeNotesPage')
);
const Announcementspage = lazyWithPreload(
  () => import('@/pages/announcements/Announcementspage')
);

// Tasks
// "Workflows" (task list templates) + "Tasks" (company tasks) now live behind
// one route ('tasks') as tabs inside TaskManagementPage. task-lists/:id (a
// single template's edit page) stays a standalone route reached from the
// Workflows tab.
const TaskManagementPage = lazyWithPreload(
  () => import('@/pages/tasks/TaskManagementPage'),
  'TaskManagementPage'
);
const AutomationsTab = lazyWithPreload(
  () => import('@/pages/tasks/automations/AutomationsTab'),
  'AutomationsTab'
);
const AutomationBuilderPage = lazyWithPreload(
  () => import('@/pages/tasks/automations/AutomationBuilderPage'),
  'AutomationBuilderPage'
);
const OnboardingTemplatesTab = lazyWithPreload(
  () => import('@/pages/tasks/tabs/OnboardingTemplatesTab'),
  'OnboardingTemplatesTab'
);
const OffboardingTemplatesTab = lazyWithPreload(
  () => import('@/pages/tasks/tabs/OffboardingTemplatesTab'),
  'OffboardingTemplatesTab'
);
const CompanyTasksTab = lazyWithPreload(
  () => import('@/pages/tasks/tabs/CompanyTasksTab'),
  'CompanyTasksTab'
);
const CreateTaskPage = lazyWithPreload(
  () => import('@/pages/tasks/CreateTaskPage'),
  'CreateTaskPage'
);
const TaskDetailPage = lazyWithPreload(
  () => import('@/pages/tasks/TaskDetailPage'),
  'TaskDetailPage'
);
const EditTaskListPage = lazyWithPreload(
  () => import('@/pages/task-lists'),
  'EditTaskListPage'
);
const AdminTasksPage = lazyWithPreload(
  () => import('@/pages/assigned-tasks/AdminTasksPage'),
  'AdminTasksPage'
);

// Timesheet
const TimesheetPage = lazyWithPreload(
  () => import('@/pages/TimesheetPage/TimesheetPage')
);
const TimesheetEmployee = lazyWithPreload(
  () => import('@/pages/TimesheetPage/TimesheetEmployee/TimesheetEmployee')
);
const RequestHistory = lazyWithPreload(
  () => import('@/components/timesheet/TimesheetDetailsModal/RequestHistory')
);

const AnalyticsPage = lazyWithPreload(
  () => import('@/pages/analytics/AnalyticsPage')
);
const PayrollExportPage = lazyWithPreload(
  () => import('@/pages/payroll-export/PayrollExportPage')
);
const PayrollExportTab = lazyWithPreload(
  () => import('@/pages/payroll-export/export/ExportTab')
);
const PayrollHistoryTab = lazyWithPreload(
  () => import('@/pages/payroll-export/history/HistoryTab')
);
const PayrollSettingsTab = lazyWithPreload(
  () => import('@/pages/payroll-export/settings/SettingsTab')
);
const PayrollCodesTab = lazyWithPreload(
  () => import('@/pages/payroll-export/codes/CodesTab')
);
const PayrollEmployeesTab = lazyWithPreload(
  () => import('@/pages/payroll-export/employees/EmployeesTab')
);

const TrainingsPage = lazyWithPreload(
  () => import('@/pages/training/TrainingsPage'),
  'TrainingsPage'
);
const TrainingBuilderPage = lazyWithPreload(
  () => import('@/pages/training/TrainingBuilderPage'),
  'TrainingBuilderPage'
);
const SurveysPage = lazyWithPreload(
  () => import('@/pages/survey/SurveysPage'),
  'SurveysPage'
);

const CreateSurveyPage = lazyWithPreload(
  () => import('@/pages/survey/CreateSurveyPage'),
  'CreateSurveyPage'
);

const SurveyDetailPage = lazyWithPreload(
  () => import('@/pages/survey/SurveyDetailPage'),
  'SurveyDetailPage'
);

const SurveyOverviewTab = lazyWithPreload(
  () => import('@/pages/survey/details/SurveyOverviewTab'),
  'SurveyOverviewTab'
);

const SurveyQuestionsTab = lazyWithPreload(
  () => import('@/pages/survey/details/SurveyQuestionsTab'),
  'SurveyQuestionsTab'
);

const SurveyResponsesRouteLayout = lazyWithPreload(
  () => import('@/pages/survey/details/response/SurveyResponsesRouteLayout'),
  'SurveyResponsesRouteLayout'
);

const SurveyResponsesTab = lazyWithPreload(
  () => import('@/pages/survey/details/SurveyResponsesTab'),
  'SurveyResponsesTab'
);

const SurveySettingsTab = lazyWithPreload(
  () => import('@/pages/survey/details/SurveySettingsTab'),
  'SurveySettingsTab'
);
const SurveyTrendsTab = lazyWithPreload(
  () => import('@/pages/survey/details/SurveyTrendsTab'),
  'SurveyTrendsTab'
);

const ResponseDetailPage = lazyWithPreload(
  () => import('@/pages/survey/details/response/ResponseDetailPage'),
  'ResponseDetailPage'
);

// Performance
const PerformanceIndexRedirect = lazyWithPreload(
  () => import('@/pages/performance/PerformanceIndexRedirect'),
  'PerformanceIndexRedirect'
);
const ReviewCyclesPage = lazyWithPreload(
  () => import('@/pages/performance/review-cycles/ReviewCyclesPage'),
  'ReviewCyclesPage'
);
const ReviewCycleEditorPage = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-editor/ReviewCycleEditorPage'),
  'ReviewCycleEditorPage'
);
const ReviewTemplatesPage = lazyWithPreload(
  () => import('@/pages/performance/review-templates/ReviewTemplatesPage'),
  'ReviewTemplatesPage'
);
const ReviewTemplateEditorPage = lazyWithPreload(
  () =>
    import('@/pages/performance/review-template-editor/ReviewTemplateEditorPage'),
  'ReviewTemplateEditorPage'
);
const ReviewCycleCreatePage = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-create/ReviewCycleCreatePage'),
  'ReviewCycleCreatePage'
);
const GoalsPage = lazyWithPreload(
  () => import('@/pages/performance/goals/GoalsPage'),
  'GoalsPage'
);
const ProbationProgramRedirect = lazyWithPreload(
  () =>
    import('@/pages/performance/evaluation-program/ProbationProgramRedirect'),
  'ProbationProgramRedirect'
);
const EvaluationProgramPage = lazyWithPreload(
  () => import('@/pages/performance/evaluation-program/EvaluationProgramPage'),
  'EvaluationProgramPage'
);
const ReviewCycleDetailPage = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-detail/ReviewCycleDetailPage'),
  'ReviewCycleDetailPage'
);
const CycleParticipantsTab = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-detail/ParticipantsTab'),
  'ParticipantsTab'
);
const CycleParticipantReviewPanel = lazyWithPreload(
  () =>
    import('@/pages/performance/review-cycle-detail/ParticipantReviewPanel'),
  'ParticipantReviewPanel'
);
const CycleResultsTab = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-detail/ResultsTab'),
  'ResultsTab'
);
const CycleFormTab = lazyWithPreload(
  () => import('@/pages/performance/review-cycle-detail/FormTab'),
  'FormTab'
);
const MyReviewsPage = lazyWithPreload(
  () => import('@/pages/performance/my-reviews/MyReviewsPage'),
  'MyReviewsPage'
);
const ManagerReviewPage = lazyWithPreload(
  () => import('@/pages/performance/manager-review/ManagerReviewPage'),
  'ManagerReviewPage'
);
const OneOnOnesPage = lazyWithPreload(
  () => import('@/pages/performance/one-on-ones/OneOnOnesPage'),
  'OneOnOnesPage'
);
const OneOnOneDetailPage = lazyWithPreload(
  () => import('@/pages/performance/one-on-one-detail/OneOnOneDetailPage'),
  'OneOnOneDetailPage'
);
// ─── Types ────────────────────────────────────────────────────────────────────

interface BaseRoute {
  path: string;
  element: React.JSX.Element;
}

export interface ChildRoute {
  index?: boolean;
  path?: string;
  element: React.JSX.Element;
  scope?: PermissionScope | PermissionScope[];
  preload?: () => Promise<unknown>;
  children?: ChildRoute[];
}

export interface MainLayoutRoute extends BaseRoute {
  scope?: PermissionScope | PermissionScope[];
  fallbackElement?: React.JSX.Element;
  children?: ChildRoute[];
  parentNav?: string;
  preload?: () => Promise<unknown>;
  redirectOnCompanySwitch?: boolean;
}

// ─── Route definitions ────────────────────────────────────────────────────────

export const authRoutes: BaseRoute[] = [
  { path: '/login', element: <LoginV2Page /> },
  { path: '/signup', element: <SignupV2Page /> },
  { path: '/forgot-password', element: <ForgotPasswordV2Page /> },
  { path: '/reset-password', element: <ResetPasswordV2Page /> },
  { path: '/signup-v2', element: <SignupV2Page /> },
  { path: '/signup-v2/workspace', element: <SelectWorkspacePage /> },
  // Deprecated: the company setup step now lives at OnboardingRoute.Company.
  {
    path: '/signup-v2/company',
    element: <Navigate to={OnboardingRoute.Company} replace />,
  },
  { path: '/verify-email', element: <VerifyPage /> },
];

export const onboardingRoutes = {
  path: OnboardingRoute.Base,
  layout: <OnboardingLayout.component />,
  children: [
    { path: OnboardingRoute.Company, element: <CompanyPage.component /> },
    {
      path: OnboardingRoute.Location,
      element: <LocationSetupPage.component />,
    },
    { path: OnboardingRoute.Features, element: <FeaturesPage.component /> },
    { path: OnboardingRoute.Logo, element: <LogoSetupPage.component /> },
  ],
};

export const mainLayoutRoutes: MainLayoutRoute[] = [
  {
    path: 'organization/messaging',
    element: <MessagingSettingsPage />,
    scope: permissionKeys.company.read,
    parentNav: 'organization',
  },
  { path: 'messages', element: <MessagesPage /> },
  {
    path: 'ats',
    element: (
      <ModuleGuard module={OnboardingFeatureKey.Recruiting}>
        <RecruitingPage.component />
      </ModuleGuard>
    ),
    preload: RecruitingPage.preload,
    scope: permissionKeys.recruiting.read,
  },
  {
    path: 'ats/applicants/:applicationId',
    element: (
      <ModuleGuard module={OnboardingFeatureKey.Recruiting}>
        <CandidateProfilePage.component />
      </ModuleGuard>
    ),
    preload: CandidateProfilePage.preload,
    scope: permissionKeys.recruiting.read,
  },
  {
    path: 'dashboard',
    element: <DashboardPage />,
    scope: permissionKeys.dashboard.read,
  },
  {
    path: 'analytics',
    element: <AnalyticsPage.component />,
    preload: AnalyticsPage.preload,
    scope: permissionKeys.analytics.read,
  },

  { path: 'profile', element: <ProfilePage /> },
  { path: 'release-notes', element: <ReleaseNotesPage.component /> },

  // Employee Routes
  {
    path: 'designation',
    element: <DesignationPage.component />,
    preload: DesignationPage.preload,
    scope: permissionKeys.designation.read,
    parentNav: 'organization',
  },
  {
    path: 'department',
    element: <DepartmentPage.component />,
    preload: DepartmentPage.preload,
    scope: permissionKeys.department.read,
    parentNav: 'organization',
  },
  {
    path: 'reporting-lines',
    element: <ReportingLinesPage.component />,
    preload: ReportingLinesPage.preload,
    scope: permissionKeys.reporting.read,
    parentNav: 'organization',
  },
  {
    path: 'reporting-exceptions',
    element: <ReportingExceptionsPage.component />,
    preload: ReportingExceptionsPage.preload,
    scope: permissionKeys.reporting.read,
    parentNav: 'organization',
  },
  {
    path: 'employee-fields',
    element: <EmployeeCustomFieldsPage.component />,
    preload: EmployeeCustomFieldsPage.preload,
    scope: permissionKeys.company.read,
    parentNav: 'organization',
  },
  {
    path: 'department/:departmentId',
    element: <DepartmentDetailsPage.component />,
    preload: DepartmentDetailsPage.preload,
    scope: permissionKeys.department.read,
  },
  {
    path: 'employees',
    element: <EmployeesPage.component />,
    preload: EmployeesPage.preload,
    scope: permissionKeys.employee.read,
  },
  {
    path: 'employees/document-requests',
    element: <CompanyDocumentRequestsPage.component />,
    preload: CompanyDocumentRequestsPage.preload,
    scope: permissionKeys.employee_document.read,
  },
  {
    path: 'employees/detail/:employeeId',
    element: <EmployeeDetailsPage.component />,
    preload: EmployeeDetailsPage.preload,
    scope: permissionKeys.employee.read,
    children: [
      { index: true, element: <Navigate to="info" replace /> },
      {
        path: 'info',
        element: <EmployeeInformation.component />,
        preload: EmployeeInformation.preload,
        scope: permissionKeys.employee.read,
      },
      {
        path: 'education',
        element: <EmployeeEducation.component />,
        preload: EmployeeEducation.preload,
        scope: permissionKeys.employee_education.read,
      },
      {
        path: 'documents',
        element: <EmployeeDocuments.component />,
        preload: EmployeeDocuments.preload,
        scope: permissionKeys.employee_document.read,
      },
      {
        path: 'history',
        element: <EmployeeLeaveHistory.component />,
        preload: EmployeeLeaveHistory.preload,
        scope: permissionKeys.employee_leave.read,
      },
      {
        path: 'family',
        element: <EmployeeFamily.component />,
        preload: EmployeeFamily.preload,
        scope: permissionKeys.employee_family.read,
      },
      {
        path: 'notes',
        element: <EmployeeNotes.component />,
        preload: EmployeeNotes.preload,
        scope: permissionKeys.employee_notes.read,
      },
      {
        path: 'assets',
        element: <EmployeeAssets.component />,
        preload: EmployeeAssets.preload,
        scope: permissionKeys.employee_asset.read,
      },
      {
        path: 'trainings',
        element: (
          <TrainingGuard>
            <EmployeeTrainings.component />
          </TrainingGuard>
        ),
        preload: EmployeeTrainings.preload,
        scope: permissionKeys.training.read,
      },
      {
        path: 'performance',
        element: (
          <PerformanceGuard>
            <EmployeePerformance.component />
          </PerformanceGuard>
        ),
        preload: EmployeePerformance.preload,
        scope: permissionKeys.performance_review.read,
      },
      {
        path: 'salary',
        element: <EmployeeSalary.component />,
        preload: EmployeeSalary.preload,
        scope: permissionKeys.employee_salary.read,
      },
      {
        path: 'compensation',
        element: <EmployeeCompensationPage.component />,
        preload: EmployeeCompensationPage.preload,
        scope: permissionKeys.employee_salary.read,
      },
      {
        path: 'bank',
        element: <EmployeeBankDetails.component />,
        preload: EmployeeBankDetails.preload,
        scope: permissionKeys.employee_bank_account.read,
      },
      {
        path: 'onboarding',
        element: <EmployeeOnboarding.component />,
        preload: EmployeeOnboarding.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'offboarding',
        element: <EmployeeOffboarding.component />,
        preload: EmployeeOffboarding.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'tasks',
        element: <EmployeeTasks.component />,
        preload: EmployeeTasks.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'activity',
        element: <EmployeeActivityLogs.component />,
        preload: EmployeeActivityLogs.preload,
        scope: permissionKeys.employee.read,
      },
    ],
  },
  {
    path: 'employees/detail/:employeeId/config/document-types',
    element: <DocumentTypeConfig.component />,
    preload: DocumentTypeConfig.preload,
    scope: permissionKeys.employee_document.read,
  },

  // Bulk Import Routes
  {
    path: 'employees/bulk-imports',
    element: <EmployeeImportJobsPage.component />,
    preload: EmployeeImportJobsPage.preload,
    scope: permissionKeys.employee.create,
  },
  {
    path: 'employees/bulk-imports/:jobId',
    element: <EmployeeImportDetailsPage.component />,
    preload: EmployeeImportDetailsPage.preload,
    scope: permissionKeys.employee.create,
  },

  {
    path: 'employee_settings',
    element: <EmployeeSettingsTabs.component />,
    preload: EmployeeSettingsTabs.preload,
    parentNav: 'organization',
    children: [
      { index: true, element: <Navigate to="access" replace /> },
      {
        path: 'access',
        element: <EmployeeAccessPage.component />,
        preload: EmployeeAccessPage.preload,
      },
      // Change rules live in the Employee access grid now (JP 2026-10-21):
      // old links land there.
      {
        path: 'approval-settings',
        element: <Navigate to="../access" replace />,
      },
      {
        path: 'approve-requests',
        element: <ApproveRequestsPage.component />,
        preload: ApproveRequestsPage.preload,
      },
    ],
  },

  // Access Management Routes
  {
    path: 'user-access',
    element: <CompanyAdminPage.component />,
    preload: CompanyAdminPage.preload,
    scope: permissionKeys.access.read,
    parentNav: 'organization',
  },
  {
    path: 'user-access/:adminId',
    element: <CompanyAdminDetailPage.component />,
    preload: CompanyAdminDetailPage.preload,
    scope: permissionKeys.access.read,
    parentNav: 'organization',
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      {
        path: 'profile',
        element: <CompanyAdminProfileTab.component />,
        preload: CompanyAdminProfileTab.preload,
      },
      {
        path: 'onboarding',
        element: <CompanyAdminOnboardingTasksTab.component />,
        preload: CompanyAdminOnboardingTasksTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'offboarding',
        element: <CompanyAdminOffboardingTasksTab.component />,
        preload: CompanyAdminOffboardingTasksTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'single_tasks',
        element: <CompanyAdminSingleTasksTab.component />,
        preload: CompanyAdminSingleTasksTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'members',
        element: <CompanyAdminManagedEmployeesTab.component />,
        preload: CompanyAdminManagedEmployeesTab.preload,
      },
      {
        path: 'activity',
        element: <CompanyAdminActivityTabRoute.component />,
        preload: CompanyAdminActivityTabRoute.preload,
      },
    ],
  },
  {
    path: 'roles',
    element: <RolesPage.component />,
    preload: RolesPage.preload,
    scope: permissionKeys.company_role.read,
    parentNav: 'organization',
  },
  {
    path: 'roles/:roleId',
    element: <EditRolePage.component />,
    preload: EditRolePage.preload,
    scope: permissionKeys.company_role.read,
  },
  // Assets Routes
  {
    path: 'asset-brands',
    element: <BrandPage.component />,
    preload: BrandPage.preload,
    scope: permissionKeys.asset_brand.read,
    parentNav: 'organization',
  },
  {
    path: 'assets',
    element: <AssetsPage.component />,
    preload: AssetsPage.preload,
    scope: permissionKeys.asset.read,
  },
  {
    path: 'assets/create',
    element: <AssetForm.component />,
    preload: AssetForm.preload,
    scope: permissionKeys.asset.create,
  },
  {
    path: 'assets/edit/:id',
    element: <AssetEditPage.component />,
    preload: AssetEditPage.preload,
    scope: permissionKeys.asset.update,
  },
  {
    path: 'assets/:id',
    element: <AssetDetailsPage.component />,
    preload: AssetDetailsPage.preload,
    scope: permissionKeys.asset.read,
  },
  {
    path: 'asset-types',
    element: <AssetTypesPage.component />,
    preload: AssetTypesPage.preload,
    scope: permissionKeys.asset_types.read,
    parentNav: 'organization',
  },
  {
    path: 'vendors',
    element: <VendorsPage.component />,
    preload: VendorsPage.preload,
    parentNav: 'organization',
  },
  {
    path: 'asset-types/:id/settings',
    element: <TypeSettingsPage.component />,
    preload: TypeSettingsPage.preload,
    scope: permissionKeys.asset_types.read,
  },

  // Leave Management Routes
  {
    path: 'leave-management',
    element: <LeaveManagementPage.component />,
    preload: LeaveManagementPage.preload,
    scope: [
      permissionKeys.leave_management.read,
      permissionKeys.leave_request.read,
      permissionKeys.leave_management.create,
      permissionKeys.leave_management.update,
    ],
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      {
        path: 'overview',
        element: <OverviewPage.component />,
        preload: OverviewPage.preload,
        scope: permissionKeys.leave_request.read,
      },
      {
        path: 'policies',
        element: <PoliciesPage.component />,
        preload: PoliciesPage.preload,
        scope: permissionKeys.leave_management.read,
      },
      {
        path: 'policies/create',
        element: <PolicyFormPage.component />,
        preload: PolicyFormPage.preload,
        scope: permissionKeys.leave_management.create,
      },
      {
        path: 'policies/:id',
        element: <PolicyFormPage.component />,
        preload: PolicyFormPage.preload,
        scope: permissionKeys.leave_management.update,
      },
      {
        path: 'employee-wallets',
        element: <LeaveWalletsPage.component />,
        preload: LeaveWalletsPage.preload,
        scope: permissionKeys.leave_management.read,
      },
      {
        path: 'one-time-leave',
        element: <GrantLeaveHistoryPage.component />,
        preload: GrantLeaveHistoryPage.preload,
        scope: permissionKeys.leave_management.read,
      },
      {
        path: 'holidays',
        element: <HolidaysPage.component />,
        preload: HolidaysPage.preload,
        scope: permissionKeys.leave_management.read,
      },
      {
        path: 'calendar',
        element: <LeaveCalendarPage.component />,
        preload: LeaveCalendarPage.preload,
        scope: permissionKeys.leave_request.read,
      },
      {
        path: 'approvals',
        element: <ApprovalRoutingPage.component />,
        preload: ApprovalRoutingPage.preload,
        scope: permissionKeys.leave_management.read,
      },
    ],
  },
  {
    path: 'leave-grant-history',
    element: <GrantLeaveHistoryPage.component />,
    preload: GrantLeaveHistoryPage.preload,
    scope: permissionKeys.leave_management.read,
  },

  // Company Info Routes
  {
    path: 'company-info',
    element: <CompanyInfoPage.component />,
    preload: CompanyInfoPage.preload,
    scope: permissionKeys.company.read,
    parentNav: 'organization',
    // No tabs since D43: General renders at /company-info itself, and the
    // Activity log moved to its own page. Old links keep working.
    children: [
      {
        index: true,
        element: <CompanyInfoGeneralTab.component />,
        preload: CompanyInfoGeneralTab.preload,
      },
      {
        path: 'general',
        element: <Navigate to="/company-info" replace />,
      },
    ],
  },
  {
    // Outside company-info so the redirect does not need company.read.
    path: 'company-info/activity',
    element: <Navigate to="/activity-log" replace />,
  },
  {
    path: 'activity-log',
    element: <CompanyActivityLogsPage.component />,
    preload: CompanyActivityLogsPage.preload,
    scope: permissionKeys.access.read,
    fallbackElement: <RedirectToDefaultRoute />,
    parentNav: 'organization',
  },
  {
    path: 'benefit-plans',
    element: <BenefitPlansPage.component />,
    preload: BenefitPlansPage.preload,
    scope: permissionKeys.benefit.read,
    parentNav: 'organization',
  },
  {
    path: 'branches',
    element: <BranchPage.component />,
    preload: BranchPage.preload,
    scope: permissionKeys.branches.read,
    parentNav: 'organization',
  },
  {
    path: 'branches/:branchId',
    element: <BranchDetailPage.component />,
    preload: BranchDetailPage.preload,
    scope: permissionKeys.branches.read,
  },
  {
    path: 'integrations',
    element: <IntegrationsPage.component />,
    preload: IntegrationsPage.preload,
    scope: [
      permissionKeys.slack_bot.read,
      permissionKeys.teams_bot.read,
      permissionKeys.mcp_integration.read,
    ],
  },
  {
    path: 'integrations/mcp',
    element: <McpIntegrationPage.component />,
    preload: McpIntegrationPage.preload,
    scope: permissionKeys.mcp_integration.read,
  },
  {
    // Old address of the MCP page, from when it only listed Claude.
    path: 'integrations/claude',
    element: <Navigate to="/integrations/mcp" replace />,
    scope: permissionKeys.mcp_integration.read,
  },
  {
    path: 'files',
    element: <FilesPage.component />,
    preload: FilesPage.preload,
    scope: permissionKeys.file_management.read,
    children: [
      {
        index: true,
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
        scope: permissionKeys.file_management.read,
      },
      {
        path: 'all',
        element: <FolderDetails.component isAll />,
        preload: FolderDetails.preload,
        scope: permissionKeys.file_management.read,
      },
      {
        path: 'bin',
        element: <FilesBin.component />,
        preload: FilesBin.preload,
        scope: permissionKeys.file_management.read,
      },
      {
        path: ':folderId',
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
        scope: permissionKeys.file_management.read,
      },
      {
        path: ':folderId/:subFolderId',
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
        scope: permissionKeys.file_management.read,
      },
    ],
  },

  // Tasks Routes
  // 'tasks' renders TaskManagementPage: a tab shell with three real child
  // routes — "Onboarding"/"Offboarding" (task list templates, formerly the
  // standalone /task-lists page) and "Tasks" (company tasks). task-lists/:id
  // stays a standalone route since editing one template is a full page, not
  // a tab.
  {
    path: 'tasks',
    element: <TaskManagementPage.component />,
    preload: TaskManagementPage.preload,
    scope: permissionKeys.task_management.read,
    children: [
      { index: true, element: <Navigate to="onboarding" replace /> },
      {
        path: 'onboarding',
        element: <OnboardingTemplatesTab.component />,
        preload: OnboardingTemplatesTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'offboarding',
        element: <OffboardingTemplatesTab.component />,
        preload: OffboardingTemplatesTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'automations',
        element: <AutomationsTab.component />,
        preload: AutomationsTab.preload,
        scope: permissionKeys.task_management.read,
      },
      {
        path: 'tasks',
        element: <CompanyTasksTab.component />,
        preload: CompanyTasksTab.preload,
        scope: permissionKeys.task_management.read,
      },
    ],
  },
  // One automation (spec 2026-10-30, 4.6): `new` or its id.
  {
    path: 'tasks/automations/:id',
    element: <AutomationBuilderPage.component />,
    preload: AutomationBuilderPage.preload,
    scope: permissionKeys.task_management.read,
  },
  {
    path: 'tasks/create',
    element: <CreateTaskPage.component />,
    preload: CreateTaskPage.preload,
    scope: permissionKeys.task_management.create,
  },
  {
    path: 'tasks/:taskId',
    element: <TaskDetailPage.component />,
    preload: TaskDetailPage.preload,
    scope: permissionKeys.task_management.read,
  },
  {
    path: 'tasks/:taskId/edit',
    element: <CreateTaskPage.component />,
    preload: CreateTaskPage.preload,
    scope: permissionKeys.task_management.update,
  },
  {
    path: 'task-lists/:id',
    element: <EditTaskListPage.component />,
    preload: EditTaskListPage.preload,
    scope: permissionKeys.task_management.read,
  },
  // No scope: any admin can be assigned a task (D181), and the page reads only
  // the viewer's own (`admin/task-inbox`).
  {
    path: 'my-tasks',
    element: <AdminTasksPage.component />,
    preload: AdminTasksPage.preload,
  },
  {
    path: 'timesheet',
    element: <TimesheetPage.component />,
    preload: TimesheetPage.preload,
    scope: permissionKeys.timesheet.read,
    children: [
      {
        index: true,
        element: <TimesheetEmployee.component />,
        preload: TimesheetEmployee.preload,
      },
      {
        path: 'requests',
        element: <RequestHistory.component />,
        preload: RequestHistory.preload,
      },
    ],
  },
  {
    path: 'payroll-export',
    element: (
      <PayrollExportGuard>
        <PayrollExportPage.component />
      </PayrollExportGuard>
    ),
    preload: PayrollExportPage.preload,
    scope: permissionKeys.payroll_export.read,
    children: [
      {
        index: true,
        element: <PayrollExportTab.component />,
        preload: PayrollExportTab.preload,
      },
      {
        path: 'history',
        element: <PayrollHistoryTab.component />,
        preload: PayrollHistoryTab.preload,
      },
      {
        path: 'codes',
        element: <PayrollCodesTab.component />,
        preload: PayrollCodesTab.preload,
      },
      {
        path: 'employees',
        element: <PayrollEmployeesTab.component />,
        preload: PayrollEmployeesTab.preload,
      },
      {
        // Read with payroll_export.read; saving needs payroll_export.update.
        path: 'settings',
        element: <PayrollSettingsTab.component />,
        preload: PayrollSettingsTab.preload,
      },
    ],
  },
  {
    path: 'notes',
    element: <NotesPage.component />,
    preload: NotesPage.preload,
    scope: [permissionKeys.hr_notes.read, permissionKeys.employee_notes.read],
    children: [
      { index: true, element: <NotesIndexRedirect /> },
      {
        path: 'admin',
        element: <AdminNotesPage.component />,
        preload: AdminNotesPage.preload,
        scope: permissionKeys.hr_notes.read,
      },
      {
        path: 'employee',
        element: <EmployeeNotesPage.component />,
        preload: EmployeeNotesPage.preload,
        scope: permissionKeys.employee_notes.read,
      },
      {
        path: 'announcements',
        element: <Announcementspage.component />,
        preload: Announcementspage.preload,
        scope: permissionKeys.announcement.read,
      },
    ],
  },

  {
    path: 'organization',
    element: <OrganizationPage.component />,
    preload: OrganizationPage.preload,
  },
  {
    // Read-only switches without company.update; the API enforces it too.
    path: 'organization/modules',
    element: <OrganizationModulesPage.component />,
    preload: OrganizationModulesPage.preload,
    scope: permissionKeys.company.read,
    parentNav: 'organization',
  },
  {
    // Only while billing applies to the company (docs/product/billing.md);
    // edits need billing.update, which the API enforces too.
    path: 'organization/billing',
    element: (
      <BillingGuard>
        <BillingPage.component />
      </BillingGuard>
    ),
    preload: BillingPage.preload,
    scope: permissionKeys.billing.read,
    parentNav: 'organization',
  },
  {
    path: 'reminders',
    element: <RemindersPage.component />,
    preload: RemindersPage.preload,
    scope: permissionKeys.employee_document.read,
    // Reached from Organisation, so it breadcrumbs under it rather than
    // showing its raw path as the page title.
    parentNav: 'organization',
  },
  {
    path: 'esignatures',
    element: <EsignaturesPage.component />,
    preload: EsignaturesPage.preload,
    scope: permissionKeys.esignature.read,
    children: [
      { index: true, element: <Navigate to="requests" replace /> },
      {
        path: 'requests',
        element: <EsignatureRequestsTab.component />,
        preload: EsignatureRequestsTab.preload,
      },
      {
        path: 'templates',
        element: <EsignatureTemplatesTab.component />,
        preload: EsignatureTemplatesTab.preload,
      },
      {
        path: 'broadcasts',
        element: <EsignatureBroadcastsTab.component />,
        preload: EsignatureBroadcastsTab.preload,
      },
    ],
  },
  {
    path: 'esignatures/requests/new',
    element: <EsignatureNewRequestPage.component />,
    preload: EsignatureNewRequestPage.preload,
    scope: permissionKeys.esignature.create,
    parentNav: 'esignatures',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'esignatures/requests/:requestId',
    element: <EsignatureRequestDetailPage.component />,
    preload: EsignatureRequestDetailPage.preload,
    scope: permissionKeys.esignature.read,
    parentNav: 'esignatures',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'esignatures/broadcasts/:broadcastId',
    element: <EsignatureBroadcastDetailPage.component />,
    preload: EsignatureBroadcastDetailPage.preload,
    scope: permissionKeys.esignature.read,
    parentNav: 'esignatures',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'esignatures/templates/new',
    element: <EsignatureTemplateEditorPage.component />,
    preload: EsignatureTemplateEditorPage.preload,
    scope: permissionKeys.esignature.create,
    parentNav: 'esignatures',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'esignatures/templates/:templateId',
    element: <EsignatureTemplateEditorPage.component />,
    preload: EsignatureTemplateEditorPage.preload,
    scope: permissionKeys.esignature.read,
    parentNav: 'esignatures',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'training',
    element: (
      <TrainingGuard>
        <TrainingsPage.component />
      </TrainingGuard>
    ),
    preload: TrainingsPage.preload,
    scope: permissionKeys.training.read,
  },
  {
    path: 'training/:trainingId',
    element: (
      <TrainingGuard>
        <TrainingBuilderPage.component />
      </TrainingGuard>
    ),
    preload: TrainingBuilderPage.preload,
    scope: permissionKeys.training.read,
    parentNav: 'training',
    redirectOnCompanySwitch: true,
  },
  {
    path: 'survey',
    element: <SurveysPage.component />,
    preload: SurveysPage.preload,
    scope: permissionKeys.survey.read,
  },
  {
    path: 'survey/create',
    element: <CreateSurveyPage.component />,
    preload: CreateSurveyPage.preload,
    scope: permissionKeys.survey.create,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'survey/:id',
    element: <SurveyDetailPage.component />,
    preload: SurveyDetailPage.preload,
    scope: permissionKeys.survey.read,
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      {
        path: 'overview',
        element: <SurveyOverviewTab.component />,
        preload: SurveyOverviewTab.preload,
      },
      {
        path: 'questions',
        element: <SurveyQuestionsTab.component />,
        preload: SurveyQuestionsTab.preload,
      },
      {
        path: 'responses',
        element: <SurveyResponsesRouteLayout.component />,
        preload: SurveyResponsesRouteLayout.preload,
        children: [
          {
            index: true,
            element: <SurveyResponsesTab.component />,
            preload: SurveyResponsesTab.preload,
          },
          {
            path: ':responseId',
            element: <ResponseDetailPage.component />,
            preload: ResponseDetailPage.preload,
          },
        ],
      },
      {
        path: 'trends',
        element: <SurveyTrendsTab.component />,
        preload: SurveyTrendsTab.preload,
      },
      {
        path: 'settings',
        element: <SurveySettingsTab.component />,
        preload: SurveySettingsTab.preload,
      },
    ],
  },
  {
    path: 'survey/:id/edit',
    element: <CreateSurveyPage.component />,
    preload: CreateSurveyPage.preload,
    scope: permissionKeys.survey.update,
  },
  // ─── Performance ───────────────────────────────────────────────────────────
  {
    path: 'performance',
    element: (
      <PerformanceGuard>
        <PerformanceIndexRedirect.component />
      </PerformanceGuard>
    ),
    preload: PerformanceIndexRedirect.preload,
  },
  {
    path: 'performance/reviews',
    element: (
      <PerformanceGuard>
        <ReviewCyclesPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewCyclesPage.preload,
    scope: permissionKeys.performance_review.read,
  },
  {
    path: 'performance/reviews/new',
    element: (
      <PerformanceGuard>
        <ReviewCycleCreatePage.component />
      </PerformanceGuard>
    ),
    preload: ReviewCycleCreatePage.preload,
    scope: permissionKeys.performance_review.create,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'performance/reviews/:cycleId',
    element: (
      <PerformanceGuard>
        <ReviewCycleDetailPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewCycleDetailPage.preload,
    scope: permissionKeys.performance_review.read,
    children: [
      { index: true, element: <Navigate to="participants" replace /> },
      {
        path: 'participants',
        element: <CycleParticipantsTab.component />,
        preload: CycleParticipantsTab.preload,
      },
      // A sibling, not a child, of `participants`: both need the detail
      // page's outlet context, which a nested <Outlet /> would reset.
      {
        path: 'participants/:reviewId',
        element: <CycleParticipantReviewPanel.component />,
        preload: CycleParticipantReviewPanel.preload,
      },
      {
        path: 'results',
        element: <CycleResultsTab.component />,
        preload: CycleResultsTab.preload,
      },
      {
        path: 'form',
        element: <CycleFormTab.component />,
        preload: CycleFormTab.preload,
      },
    ],
  },
  {
    path: 'performance/reviews/:cycleId/edit',
    element: (
      <PerformanceGuard>
        <ReviewCycleEditorPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewCycleEditorPage.preload,
    scope: permissionKeys.performance_review.update,
  },
  {
    path: 'performance/templates',
    element: (
      <PerformanceGuard>
        <ReviewTemplatesPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewTemplatesPage.preload,
    scope: permissionKeys.performance_review.read,
  },
  {
    path: 'performance/templates/new',
    element: (
      <PerformanceGuard>
        <ReviewTemplateEditorPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewTemplateEditorPage.preload,
    scope: permissionKeys.performance_review.create,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'performance/templates/:templateId/edit',
    element: (
      <PerformanceGuard>
        <ReviewTemplateEditorPage.component />
      </PerformanceGuard>
    ),
    preload: ReviewTemplateEditorPage.preload,
    scope: permissionKeys.performance_review.update,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'performance/my-reviews',
    element: (
      <PerformanceGuard>
        <MyReviewsPage.component />
      </PerformanceGuard>
    ),
    preload: MyReviewsPage.preload,
  },
  {
    path: 'performance/my-reviews/:reviewId',
    element: (
      <PerformanceGuard>
        <ManagerReviewPage.component />
      </PerformanceGuard>
    ),
    preload: ManagerReviewPage.preload,
  },
  {
    path: 'performance/goals',
    element: (
      <PerformanceGuard>
        <GoalsPage.component />
      </PerformanceGuard>
    ),
    preload: GoalsPage.preload,
    scope: permissionKeys.performance_review.read,
  },
  {
    // The old Probation tab: HR emails and notifications still link here.
    path: 'performance/probation',
    element: (
      <PerformanceGuard>
        <ProbationProgramRedirect.component />
      </PerformanceGuard>
    ),
    preload: ProbationProgramRedirect.preload,
    scope: permissionKeys.performance_review.read,
  },
  {
    path: 'performance/programs/new',
    element: (
      <PerformanceGuard>
        <EvaluationProgramPage.component />
      </PerformanceGuard>
    ),
    preload: EvaluationProgramPage.preload,
    scope: permissionKeys.performance_review.update,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'performance/programs/:programId',
    element: (
      <PerformanceGuard>
        <EvaluationProgramPage.component />
      </PerformanceGuard>
    ),
    preload: EvaluationProgramPage.preload,
    scope: permissionKeys.performance_review.read,
    redirectOnCompanySwitch: true,
  },
  {
    path: 'performance/one-on-ones',
    element: (
      <PerformanceGuard>
        <OneOnOnesPage.component />
      </PerformanceGuard>
    ),
    preload: OneOnOnesPage.preload,
  },
  {
    path: 'performance/one-on-ones/:oneOnOneId',
    element: (
      <PerformanceGuard>
        <OneOnOneDetailPage.component />
      </PerformanceGuard>
    ),
    preload: OneOnOneDetailPage.preload,
  },
];

interface DefaultRoutesByPermission {
  path: string;
  scope: PermissionScope;
}
export const defaultRoutesByPermission: DefaultRoutesByPermission[] = [
  { path: '/dashboard', scope: permissionKeys.dashboard.read },
  { path: '/employees', scope: permissionKeys.employee.read },
  { path: '/department', scope: permissionKeys.department.read },
  { path: '/designation', scope: permissionKeys.designation.read },
  { path: '/asset-brands', scope: permissionKeys.asset_brand.read },
  { path: '/user-access', scope: permissionKeys.access.read },
  { path: '/roles', scope: permissionKeys.company_role.read },
  { path: '/company-info', scope: permissionKeys.company.read },
];
